/**
 * Campus-life imagery, ingested from the institute's own pages through
 * scripts/ingest-images.py into sites/biet/campus-life/… (2026-08-07).
 *
 * Generated by scripts, then hand-corrected where a caption did not match the
 * frame (see the dispensary note below). Every URL below is a key in the media bucket;
 * <Figure> resolves it through the transform CDN. Dimensions are the ingested
 * ones, not the originals: the pipeline caps the long edge at 2560, which most
 * of the auditorium and sports frames came down from.
 *
 * Alt text comes from what the source named the file — its sports gallery ships
 * captions inside the filenames ("College_Badminton_team_Girls.jpg") — or, where
 * the filename says nothing, from the page the image was published on, checked
 * against the picture. The dispensary set is captioned "health camp" rather than
 * "health centre" because that is what every frame in it shows — blood-donation
 * drives and a yoga session, not the clinic.
 *
 * The source republishes several frames under different names. Verified by MD5:
 * health-01 is the same file as red-cross-01; auditorium-10 and -11 repeat -09;
 * -13 repeats -12; -16 repeats -15. Each duplicate stays in the bucket but is
 * left out of the lists below, so no page can show one photograph twice.
 *
 * Five SAC gallery images the source links (sac2-sac6.png) 404 on its own server
 * and are absent here.
 */
import type { Media } from "@zivin-labs/site-content";

export const SAC_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/sac/collage1.jpg", alt: "A Students' Academic Council event", width: 1280, height: 960 },
  { url: "sites/biet/campus-life/sac/collage3.png", alt: "A Students' Academic Council event", width: 1440, height: 1080 },
  { url: "sites/biet/campus-life/sac/sac-01.png", alt: "A Students' Academic Council event", width: 602, height: 443 },
];

export const GYM_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/gym/gym-01.jpg", alt: "The multi gym on campus", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/gym/gym-02.jpg", alt: "The multi gym on campus", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/gym/gym-03.jpg", alt: "The multi gym on campus", width: 2560, height: 1707 },
];

export const AUDITORIUM_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/auditorium/auditorium-01.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1718 },
  { url: "sites/biet/campus-life/auditorium/auditorium-02.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/auditorium/auditorium-03.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/auditorium/auditorium-04.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 1707, height: 2560 },
  { url: "sites/biet/campus-life/auditorium/auditorium-05.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/auditorium/auditorium-06.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/auditorium/auditorium-07.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/auditorium/auditorium-08.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/auditorium/auditorium-09.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/auditorium/auditorium-12.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1440 },
  { url: "sites/biet/campus-life/auditorium/auditorium-14.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1440 },
  { url: "sites/biet/campus-life/auditorium/auditorium-15.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1440 },
  { url: "sites/biet/campus-life/auditorium/auditorium-17.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1440 },
  { url: "sites/biet/campus-life/auditorium/auditorium-18.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1440 },
  { url: "sites/biet/campus-life/auditorium/auditorium-19.jpg", alt: "The S. S. Mallikarjun Cultural Centre", width: 2560, height: 1706 },
];

export const SPORTS_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/sports/college-athletic-team.jpg", alt: "The college athletics team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-athletic-team-1.jpg", alt: "The college athletics team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-athletic-team-2.jpg", alt: "The college athletics team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-badminton-team.jpg", alt: "The college badminton team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-badminton-team-girls.jpg", alt: "The women's badminton team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-baskete-ball-team-girls.jpg", alt: "The women's basketball team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-baskete-ball-team-mens.jpg", alt: "The men's basketball team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-kabaddi-team.jpg", alt: "The college kabaddi team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-kabaddi-team-1.jpg", alt: "The college kabaddi team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-throw-ball-team.jpg", alt: "The college throwball team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-football-team.jpg", alt: "The college football team", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/college-team-with-non-teaching-staff.jpg", alt: "A college team with non-teaching staff", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/group-photos.jpg", alt: "Team group photograph", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/independance-day.jpg", alt: "Independence Day on campus", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/sports/match-at-vtu-level.jpg", alt: "A match at VTU level", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/performe-in-vtu-level.jpg", alt: "Competing at VTU level", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/playing-in-sit-campus.jpg", alt: "Playing at the SIT campus", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/throw-ball.jpg", alt: "Throwball", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/sports/throw-ball-2.jpg", alt: "Throwball", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/sports/throw-ball-3.jpg", alt: "Throwball", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/sports/vtu-matches.jpg", alt: "A VTU-level match", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/vtu-matches-2.jpg", alt: "A VTU-level match", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/perform-in-athletic.jpg", alt: "Competing in athletics", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/sports/sports-01.jpg", alt: "Sport at BIET", width: 960, height: 1280 },
  { url: "sites/biet/campus-life/sports/sports-02.jpg", alt: "Sport at BIET", width: 1066, height: 1600 },
  { url: "sites/biet/campus-life/sports/sports-03.jpg", alt: "Sport at BIET", width: 1200, height: 1600 },
  { url: "sites/biet/campus-life/sports/sports-04.jpg", alt: "Sport at BIET", width: 875, height: 1348 },
  { url: "sites/biet/campus-life/sports/sports-05.jpg", alt: "Sport at BIET", width: 433, height: 236 },
  { url: "sites/biet/campus-life/sports/sports-06.jpg", alt: "Sport at BIET", width: 1600, height: 1066 },
  { url: "sites/biet/campus-life/sports/sports-07.jpg", alt: "Sport at BIET", width: 1600, height: 1200 },
];

export const NCC_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/ncc/ncc-01.jpg", alt: "NCC cadets at BIET", width: 780, height: 1040 },
  { url: "sites/biet/campus-life/ncc/ncc-02.jpg", alt: "NCC cadets at BIET", width: 774, height: 1032 },
  { url: "sites/biet/campus-life/ncc/ncc-03.jpg", alt: "NCC cadets at BIET", width: 1032, height: 581 },
  { url: "sites/biet/campus-life/ncc/ncc-04.jpg", alt: "NCC cadets at BIET", width: 1040, height: 780 },
  { url: "sites/biet/campus-life/ncc/ncc-05.jpg", alt: "NCC cadets on a campus clean-up drive", width: 1280, height: 576 },
  { url: "sites/biet/campus-life/ncc/ncc-06.jpg", alt: "NCC cadets at BIET", width: 1032, height: 774 },
];

/**
 * The NSS page publishes no field photography at all — its only image is the
 * officer's thumbnail, which is in OFFICER_PORTRAITS below.
 */
export const NSS_PHOTOS: Media[] = [];

export const RED_CROSS_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/red-cross/red-cross-01.jpg", alt: "An NSS blood-donation camp on campus", width: 693, height: 462 },
  { url: "sites/biet/campus-life/red-cross/red-cross-02.jpg", alt: "Youth Red Cross Wing activity at BIET", width: 701, height: 463 },
  { url: "sites/biet/campus-life/red-cross/red-cross-03.jpg", alt: "Youth Red Cross Wing activity at BIET", width: 699, height: 462 },
  { url: "sites/biet/campus-life/red-cross/red-cross-04.jpg", alt: "Youth Red Cross Wing activity at BIET", width: 693, height: 462 },
  { url: "sites/biet/campus-life/red-cross/red-cross-05.jpg", alt: "Youth Red Cross Wing activity at BIET", width: 821, height: 462 },
  { url: "sites/biet/campus-life/red-cross/red-cross-06.jpg", alt: "Youth Red Cross Wing activity at BIET", width: 780, height: 462 },
];

/** From bietdvg.edu/library/gallery — the only images the library publishes. */
export const LIBRARY_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/library/library-01.png", alt: "The Central Library building", width: 2048, height: 1360 },
  { url: "sites/biet/campus-life/library/library-02.png", alt: "Inside the Central Library", width: 1520, height: 1152 },
  { url: "sites/biet/campus-life/library/library-03.png", alt: "Inside the Central Library", width: 1082, height: 820 },
  { url: "sites/biet/campus-life/library/library-04.png", alt: "Inside the Central Library", width: 1063, height: 806 },
  { url: "sites/biet/campus-life/library/library-05.png", alt: "Inside the Central Library", width: 1520, height: 1152 },
];

/**
 * Programme-officer portraits, kept apart from the field photography above.
 * The source files these in the same galleries, but they are 128-264px
 * thumbnails: fine beside a name, ruinous in a gallery tile, and they would
 * otherwise be photos[0] for two of the three wings.
 */
export const OFFICER_PORTRAITS: Record<string, Media> = {
  nss: { url: "sites/biet/campus-life/nss/krishna-kumar.jpg", alt: "T. K. Krishna Kumar, NSS Programme Officer", width: 165, height: 163 },
  ncc: { url: "sites/biet/campus-life/ncc/lt20anjaneya.jpg", alt: "Lt. Anjaneya L.H., Associate NCC Officer", width: 220, height: 264 },
  "red-cross": { url: "sites/biet/campus-life/red-cross/red-cross-07.jpg", alt: "Dr. S. M. Chandrasekhara, YRCW Programme Officer", width: 128, height: 110 },
};

export const HEALTH_PHOTOS: Media[] = [
  // health-01 is byte-for-byte the frame the Red Cross page also publishes
  // (red-cross-01). It stays in the bucket but is left out of this list so the
  // page cannot show the same photograph twice.
  { url: "sites/biet/campus-life/health/health-02.jpg", alt: "A health camp on campus", width: 693, height: 462 },
  { url: "sites/biet/campus-life/health/health-03.jpg", alt: "A health camp on campus", width: 693, height: 462 },
  { url: "sites/biet/campus-life/health/health-04.jpg", alt: "A health camp on campus", width: 580, height: 407 },
  { url: "sites/biet/campus-life/health/health-05.jpg", alt: "A health camp on campus", width: 580, height: 407 },
  { url: "sites/biet/campus-life/health/health-06.jpg", alt: "A health camp on campus", width: 583, height: 398 },
];

export const CANTEEN_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/canteen/canteen-01.jpg", alt: "The college canteen", width: 1455, height: 970 },
  { url: "sites/biet/campus-life/canteen/canteen-02.jpg", alt: "The college canteen", width: 1455, height: 970 },
  { url: "sites/biet/campus-life/canteen/canteen-03.jpg", alt: "The college canteen", width: 1455, height: 970 },
];

export const LADIES_REST_ROOM_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/ladies-rest-room/ladies-rest-room-01.jpg", alt: "The ladies' rest room in the Biotechnology block", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/ladies-rest-room/ladies-rest-room-02.jpg", alt: "The ladies' rest room in the Biotechnology block", width: 2560, height: 1707 },
  { url: "sites/biet/campus-life/ladies-rest-room/ladies-rest-room-03.jpg", alt: "The ladies' rest room in the Biotechnology block", width: 2560, height: 1707 },
];

export const TRANSPORT_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/transport/transport-01.jpg", alt: "City buses serving the campus", width: 448, height: 301 },
  { url: "sites/biet/campus-life/transport/transport-02.jpg", alt: "City buses serving the campus", width: 448, height: 301 },
];

export const WATER_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/water/ro-unit.jpg", alt: "A reverse-osmosis drinking-water unit", width: 2560, height: 1144 },
];

export const WASTE_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/waste/waste-01.jpg", alt: "Waste management on campus", width: 458, height: 786 },
  { url: "sites/biet/campus-life/waste/waste-02.jpg", alt: "Waste management on campus", width: 1152, height: 560 },
  { url: "sites/biet/campus-life/waste/waste-03.jpg", alt: "Waste management on campus", width: 960, height: 1280 },
];

export const GREEN_CAMPUS_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/green-campus/green-campus-01.jpg", alt: "BIET Initiatives for Green campus", width: 719, height: 949 },
];

export const NAIN_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/nain/nain-img.jpg", alt: "The NAIN incubation centre", width: 1000, height: 751 },
];

export const EDC_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/edc/dr-sujith-s-h-1-p92avqw41tv7lxh4uuzkntjlq449b8c8ge712ffov4.jpg", alt: "Dr. Sujith Kumar S H, EDC convener", width: 300, height: 400 },
  { url: "sites/biet/campus-life/edc/t-ec-02-dr-g-s-sunitha.jpg", alt: "Dr. G S Sunitha, EDC co-convener", width: 1991, height: 2560 },
];

export const SKILL_CENTRE_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/skill-centre/t-me-07-dr-sharan-a-s.jpg", alt: "Dr. Sharan A S, Skill Development Centre co-ordinator", width: 1991, height: 2560 },
];

export const GUEST_HOUSE_PHOTOS: Media[] = [
  { url: "sites/biet/campus-life/guest-house/guest-house-01.jpg", alt: "The Silver-Line Guest House", width: 2048, height: 1360 },
  { url: "sites/biet/campus-life/guest-house/guest-house-02.jpg", alt: "The Silver-Line Guest House", width: 2048, height: 1360 },
  { url: "sites/biet/campus-life/guest-house/guest-house-03.jpg", alt: "The Silver-Line Guest House", width: 2048, height: 1360 },
  { url: "sites/biet/campus-life/guest-house/guest-house-04.jpg", alt: "The Silver-Line Guest House", width: 2048, height: 1360 },
  { url: "sites/biet/campus-life/guest-house/guest-house-05.jpg", alt: "The Silver-Line Guest House", width: 2560, height: 1718 },
  { url: "sites/biet/campus-life/guest-house/guest-house-06.jpg", alt: "The Silver-Line Guest House", width: 2560, height: 1707 },
];
